import { facetCount, internal } from '@croissants/contracts';
import {
  Button,
  Card,
  FillPage,
  FilterMenu,
  orderSummaryReasonText,
  pillWord,
  SortHeader,
  type TableColumns,
  TableUnit,
  useEntryState,
  useTable,
} from '@croissants/ui';
import { type JSX, useMemo, useRef } from 'react';
import { Link } from 'wouter';
import { FilterBar, SelectField, TextField } from '../components/inputs';
import { RiskCell } from '../components/RiskCell';
import { ScreenHeader } from '../components/Shell';
import { OrderStatePill, StatusPill } from '../components/StatusPill';
import { AsyncPanel, LoadingPanel } from '../components/states';
import { useLocale, useT } from '../i18n/index';
import { useEndpointQuery } from '../lib/api';
import { time as formatClock, window as formatWindow } from '../lib/format';
import { bakeryPickerLabel } from '../lib/health';
import {
  type DashboardRow,
  latenessBandOf,
  latenessDetailMinutes,
  severityOf,
  sortByLatenessRisk,
  tally,
} from '../lib/lateness';
import { usePagerLabels } from '../lib/pager-labels';
import { pillFilterMenu } from '../lib/pill-filter';
import { usePlatformDay } from '../lib/platform-day';

/**
 * The admin dashboard (PRD OPS-001): every order for the service day, bakery
 * readiness and provider jobs, sorted by lateness risk (RTE-006).
 *
 * It is the only screen in the portal that polls. A morning service is decided in
 * fifteen-minute increments, so the board refreshes on a timer and says so - and
 * the operator can stop it, because a list that reshuffles while a name is being
 * read is worse than one that is thirty seconds old.
 */

const REFRESH_SECONDS = 15;

const ORDER_STATUSES = internal.DashboardRow.shape.summary.options;

type BoardColumn =
  | 'window'
  | 'order'
  | 'property'
  | 'bakery'
  | 'status'
  | 'delivery'
  | 'courier'
  | 'risk';

/**
 * What each heading sorts on.
 *
 * Three of the eight columns print a word - the order's state, the delivery
 * job's state and the risk band - and ordering any of them by the wire code
 * would order the column by a string no reader can see, so each sorts on the
 * word its pill shows (`pillWord`, #2004).
 *
 * The risk column sorts on the MINUTES, not on the band: the band is five
 * values and the minutes are what separates two rows inside one of them, which
 * is the question an operator has of that column. The band is still what the
 * column SAYS, so it is what search reads (`text`) - typing "late" narrows the
 * board to the rows whose badge says it.
 */
const BOARD_COLUMNS: TableColumns<DashboardRow, BoardColumn> = {
  window: { value: (row) => row.promisedWindowStart, kind: 'date' },
  order: { value: (row) => row.shortCode },
  property: { value: (row) => row.propertyReference },
  bakery: { value: (row) => row.bakeryName },
  status: {
    value: (row) => pillWord('orderSummary', row.summary, 'en'),
    // The reason beside the state is searchable too: "card refused" finds them.
    text: (row) =>
      [
        pillWord('orderSummary', row.summary, 'en'),
        orderSummaryReasonText(
          row.summary,
          { ...row.lifecycle, courierSearchDeadline: row.courierSearchDeadline },
          'en',
          (instant) => formatClock(instant, 'en'),
        ) ?? '',
      ]
        .join(' ')
        .trim(),
  },
  delivery: {
    value: (row) =>
      row.delivery === null ? null : pillWord('courierJob', row.delivery.status, 'en'),
  },
  courier: { value: (row) => row.delivery?.courier?.firstName ?? null },
  risk: {
    value: (row) => latenessDetailMinutes(row),
    kind: 'number',
    text: (row) => {
      const band = latenessBandOf(severityOf(row));
      return band === null ? '' : pillWord('latenessBand', band, 'en');
    },
  },
};

export function DashboardScreen(): JSX.Element {
  const t = useT();
  const { tCode, tKey, locale } = useLocale();

  // The platform's service day, never the browser's (TST-003): the rehearsal
  // clock decides "today", and ?serviceDay= in the URL is honoured.
  const { day: serviceDay, explicitDay, setDay: setServiceDay } = usePlatformDay();
  const [summary, setSummary] = useEntryState<readonly string[]>('summaries', []);
  const [bakeries, setBakeries] = useEntryState<readonly string[]>('bakeries', []);
  const [flaggedOnly, setFlaggedOnly] = useEntryState('flaggedOnly', false);
  const [live, setLive] = useEntryState('live', true);

  const query = useEndpointQuery(
    internal.getDashboard,
    {
      query: {
        limit: 200,
        // Only an EXPLICIT day (typed or from the URL) is sent. Omitted, the
        // endpoint serves the platform's own Paris today from the same clock
        // `/internal/time` reads - the identical answer - so the first fetch needn't
        // wait a round trip for `/internal/time`, and the query key doesn't change
        // (no second fetch) when that probe lands and fills the date input.
        ...(explicitDay === '' ? {} : { serviceDay: explicitDay }),
        ...(summary.length === 0 ? {} : { summary: [...summary] as DashboardRow['summary'][] }),
        ...(bakeries.length === 0 ? {} : { bakeryLocationId: [...bakeries] }),
        ...(flaggedOnly ? { flaggedOnly: true } : {}),
      },
    },
    {
      refetchInterval: live ? REFRESH_SECONDS * 1000 : false,
      staleTime: 0,
    },
  );

  /*
   * What the platform is SELLING (OPS-025). Both switches off and no guest can
   * order anywhere, which on this screen presents as a board that simply stops
   * gaining rows - a state an operator would otherwise have to infer from an
   * absence. It is stated beside the title instead.
   *
   * Its own query, and deliberately not part of `getDashboard`: the board is one
   * service day and this is a platform-wide fact that changes about never, so it
   * is read once per visit rather than every fifteen seconds. A failure here is
   * silent by design - the badge is absent, and the board it sits above is
   * unaffected.
   */
  const ordering = useEndpointQuery(internal.getOrderingModes, {});
  const orderingClosed = ordering.data?.preorder === false && ordering.data.immediate === false;

  /*
   * The board's own order, before any heading is clicked: the lateness ranking
   * (RTE-006), which is what this screen exists to put first. `initial: null`
   * is what keeps it - the hook leaves the rows as it found them until the
   * reader asks for something else.
   */
  const rows = useMemo(() => sortByLatenessRisk(query.data?.rows.items ?? []), [query.data]);
  const columns = BOARD_COLUMNS;
  /*
   * The reset key is the four filters this screen SENDS, so changing one asks a
   * different question and the answer starts on page one - while the fifteen-
   * second poll behind an unchanged filter leaves the reader where they were,
   * which is the whole reason the key is the filters and not the fetch.
   */
  const table = useTable(rows, {
    columns,
    resetKey: `${explicitDay}|${summary.join(',')}|${bakeries.join(',')}|${flaggedOnly ? 'flagged' : ''}`,
    more: query.data?.rows.nextCursor != null,
    locale,
  });
  const pagerLabels = usePagerLabels();

  /*
   * The filter options, counted by the endpoint over the day's WHOLE board
   * rather than the page held (`facets`): an option no order carries is not
   * offered. The last answer is kept while the next one loads, so a panel does
   * not empty itself for the moment a tick takes to come back.
   */
  const answeredFacets = query.data?.facets;
  const lastFacets = useRef(answeredFacets);
  if (answeredFacets !== undefined) lastFacets.current = answeredFacets;
  const facets = answeredFacets ?? lastFacets.current;
  const filterLabels = {
    search: t('filter.search'),
    selectAll: t('filter.selectAll'),
    clearAll: t('filter.clearAll'),
  };
  // One option per word: Awaiting bakery is PLACED and AWAITING_BAKERY, Canceled three states.
  const statusFilter = pillFilterMenu('orderSummary', ORDER_STATUSES, facets?.summary);
  /* Every shop the day holds orders for, plus any ticked shop that holds none
     now - a ticked value must stay visible to be unticked. */
  const shopNames = new Map(
    (query.data?.bakeryReadiness ?? []).map((shop) => [shop.locationId, bakeryPickerLabel(shop)]),
  );
  const bakeryOptions = [
    ...new Set([...(facets?.bakery ?? []).map((facet) => facet.value), ...bakeries]),
  ]
    .map((id) => ({
      value: id,
      label: shopNames.get(id) ?? rows.find((row) => row.bakeryLocationId === id)?.bakeryName ?? id,
      count: facetCount(facets?.bakery, id),
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  return (
    /*
     * A fill screen like every other list, and the board is the LAST thing in
     * the column - which is the condition fill mode needs, and the one this
     * screen broke while the active bakeries table sat under the board. A
     * natural-height sibling after a fill table does not yield, so the board
     * yielded all of it: measured on the demo portal at 1407x840 on 2026-09-09,
     * a scrollport TWO PIXELS tall holding the morning's only order needing an
     * operator. That table is the Bakeries section's first tab now
     * (`ActiveBakeries.tsx`), and nothing may be put back under the board
     * (`Dashboard.test.tsx` refuses the shape).
     */
    <FillPage>
      <ScreenHeader
        title={
          orderingClosed ? (
            <span className="internal-inline">
              {t('dashboard.title')}
              <StatusPill family="tag" state="ORDERING_CLOSED" />
            </span>
          ) : (
            t('dashboard.title')
          )
        }
        info={t('dashboard.subtitle')}
        actions={
          <>
            <span className="internal-muted internal-phrase">
              {live ? t('dashboard.live', { seconds: REFRESH_SECONDS }) : t('dashboard.paused')}
            </span>
            <Button variant="secondary" size="sm" onClick={() => setLive((value) => !value)}>
              {live ? t('dashboard.pause') : t('dashboard.resume')}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => query.refetch()}
              loading={query.isFetching}
              loadingLabel={t('state.loading')}
            >
              {t('action.refresh')}
            </Button>
          </>
        }
      />

      <FilterBar table={table} searchLabel={t('action.search')}>
        <TextField
          label={t('field.serviceDay')}
          type="date"
          value={serviceDay}
          onChange={setServiceDay}
        />
        <FilterMenu
          label={t('dashboard.filter.status')}
          values={statusFilter.shown(summary)}
          onChange={(next) => setSummary(statusFilter.chosen(next))}
          labels={filterLabels}
          options={statusFilter.options}
        />
        <FilterMenu
          label={t('dashboard.filter.bakery')}
          values={bakeries}
          onChange={setBakeries}
          labels={filterLabels}
          options={bakeryOptions}
        />
        <SelectField
          label={t('dashboard.flaggedOnly')}
          value={flaggedOnly ? 'yes' : ''}
          onChange={(next) => setFlaggedOnly(next === 'yes')}
          options={[{ value: 'yes', label: t('dashboard.flaggedOnly') }]}
          includeEmpty
        />
      </FilterBar>

      {/* Eight columns of orders: the placeholder holds their height so the row
          an operator is reaching for does not move when the data lands. */}
      <AsyncPanel query={query} pending={<LoadingPanel skeleton={{ rows: 8, columns: 8 }} />}>
        {(data) => {
          const counts = tally(rows);
          return (
            <>
              <div className="internal-tally">
                <TallyTile label={t('tally.late')} value={counts.late} tone="danger" />
                <TallyTile label={t('tally.atRisk')} value={counts.atRisk} tone="warning" />
                <TallyTile label={t('tally.watch')} value={counts.watch} tone="info" />
                <TallyTile label={t('tally.onTrack')} value={counts.onTrack} tone="success" />
                {/* The fourth band (OPS-001): cancelled, rejected or refunded.
                    They used to be counted On track, which is the one thing
                    they demonstrably are not. */}
                <TallyTile label={t('tally.closed')} value={counts.closed} tone="neutral" />
                <TallyTile
                  label={t('tally.incidents')}
                  value={counts.openIncidents}
                  tone={counts.openIncidents > 0 ? 'warning' : 'neutral'}
                />
              </div>

              <p className="internal-muted">
                {t('dashboard.count', { count: rows.length })} &middot;{' '}
                {t('dashboard.serverTime', { time: formatClock(data.serverTime, locale) })}
              </p>

              {rows.length === 0 ? (
                <Card flat>
                  <p className="internal-muted">{t('state.empty')}</p>
                </Card>
              ) : (
                <TableUnit
                  table={table}
                  fill
                  label={t('dashboard.title')}
                  labels={pagerLabels}
                  caption={t('table.rows', { count: table.slice.total })}
                >
                  <caption className="cib-visually-hidden">{t('dashboard.title')}</caption>
                  <thead>
                    <tr>
                      <SortHeader table={table} column="window">
                        {t('dashboard.column.window')}
                      </SortHeader>
                      <SortHeader table={table} column="order">
                        {t('dashboard.column.order')}
                      </SortHeader>
                      <SortHeader table={table} column="property">
                        {t('dashboard.column.property')}
                      </SortHeader>
                      <SortHeader table={table} column="bakery">
                        {t('dashboard.column.bakery')}
                      </SortHeader>
                      <SortHeader table={table} column="status">
                        {t('dashboard.column.status')}
                      </SortHeader>
                      <SortHeader table={table} column="delivery">
                        {t('dashboard.column.delivery')}
                      </SortHeader>
                      <SortHeader table={table} column="courier">
                        {t('dashboard.column.courier')}
                      </SortHeader>
                      <SortHeader table={table} column="risk">
                        {t('dashboard.column.risk')}
                      </SortHeader>
                    </tr>
                  </thead>
                  <tbody>
                    {table.rows.map((row) => (
                      <tr
                        key={row.orderId}
                        className={`internal-row internal-row--${severityOf(row)}`}
                      >
                        <td className="internal-nowrap">
                          <span className="internal-strong">
                            {formatWindow(row.promisedWindowStart, row.promisedWindowEnd, locale)}
                          </span>
                          <br />
                          <span className="internal-muted">
                            {row.estimatedArrival === null
                              ? t('dashboard.etaUnknown')
                              : t('dashboard.eta', {
                                  time: formatClock(row.estimatedArrival, locale),
                                })}
                          </span>
                        </td>
                        <td>
                          <Link
                            href={`/orders/${row.orderId}`}
                            className="internal-link internal-mono"
                          >
                            {row.shortCode}
                          </Link>
                          {row.fulfillmentMode === 'IMMEDIATE' ? (
                            <>
                              {' '}
                              <StatusPill family="tag" state="NOW" />
                            </>
                          ) : null}
                          <br />
                          {/* The APARTMENT's menu type, which since 2026-09-20
                              has three values and not two: mode B split into
                              B1 and B2 (OPS-013). `modes.tag.*` is the one
                              place those three words are written. */}
                          <span className="internal-muted">
                            {tKey(`modes.tag.${row.storefrontMode}`)}
                          </span>
                        </td>
                        <td>{row.propertyReference}</td>
                        <td>{row.bakeryName ?? <em>{t('dashboard.noBakery')}</em>}</td>
                        <td>
                          {/* The state in its own colour, and - when an
                              operator must act - "Needs an operator" as a red
                              line under it: a word has one colour, so the row's
                              urgency is text, not the pill's tone (#2004). */}
                          <OrderStatePill
                            state={row.summary}
                            lifecycle={row.lifecycle}
                            courierSearchDeadline={row.courierSearchDeadline}
                          />
                          {row.needsAction ? (
                            <>
                              <br />
                              <span className="internal-bad">{t('orders.needsAction')}</span>
                            </>
                          ) : null}
                          {/* WHY it stopped, from the closed set the
                                  counter refuses from (BAK-003, OPS-002). The
                                  row has carried the code since the reason
                                  became a field and no surface rendered it, so
                                  "Rejected" was the whole of what an operator
                                  could read. The baker's own note follows it
                                  when there is one. */}
                          {row.rejectionReasonCode === null ? null : (
                            <>
                              <br />
                              <span className="internal-muted">
                                {tCode('reject', row.rejectionReasonCode)}
                              </span>
                            </>
                          )}
                          {row.rejectionNote === null || row.rejectionNote === '' ? null : (
                            <>
                              <br />
                              <span className="internal-muted">{row.rejectionNote}</span>
                            </>
                          )}
                        </td>
                        <td>
                          {row.delivery === null ? (
                            <span className="internal-muted">{t('dashboard.noDelivery')}</span>
                          ) : (
                            <>
                              <StatusPill family="courierJob" state={row.delivery.status} />
                              <br />
                              <span className="internal-muted internal-mono">
                                {row.delivery.providerName}
                              </span>
                            </>
                          )}
                        </td>
                        <td>
                          {row.delivery?.courier?.firstName ?? (
                            <span className="internal-muted">{t('dashboard.noCourier')}</span>
                          )}
                        </td>
                        <td>
                          <RiskCell row={row} />
                          {row.openIncidents > 0 ? (
                            <span className="internal-muted">
                              {t('dashboard.openIncidents', { count: row.openIncidents })}
                            </span>
                          ) : null}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </TableUnit>
              )}
            </>
          );
        }}
      </AsyncPanel>
    </FillPage>
  );
}

function TallyTile({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: 'danger' | 'warning' | 'info' | 'success' | 'neutral';
}): JSX.Element {
  return (
    <div className={`internal-tile internal-tile--${tone}`}>
      <span className="internal-tile__value">{value}</span>
      <span className="internal-tile__label">{label}</span>
    </div>
  );
}
